import classNames from "classnames";
import React, { CSSProperties, useMemo, useState } from "react";
import {
  getElementScreenPosition,
  isWebsiteBuilderStretchElementData,
  isWebsiteBuilderTextElementType,
  IWebsiteBuilderElementStretch,
  TWebsiteBuilderElement,
  WEBSITE_BUILDER_ELEMENT_CLASS_NAME,
} from "../../../../nodes/element/websiteBuilderElement";
import { useWebsiteBuilderContext } from "../../../../websiteBuilderContext";
import {
  IWebsiteBuilderNodeDimensions,
  IWebsiteBuilderNodePosition,
  TWebsiteBuilderElementParentNode,
} from "../../../../nodes/websiteBuilderNode";
import { useTreeRefMapRegistration } from "../../../../hooks/treeRefMap/useTreeRefMapRegistration";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderSelectNode,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { ElementHoverTitle } from "./ElementHoverTitle";
import { IUseElementViewMoveArgs, useElementViewMove } from "../../hooks/element/useElementViewMove/useElementViewMove";
import {
  IUseElementViewResizeArgs,
  useElementViewResize,
} from "../../hooks/element/useElementViewResize/useElementViewResize";
import {
  IUseElementViewSettingsArgs,
  useElementViewSettings,
} from "../../hooks/element/useElementViewSettings/useElementViewSettings";
import { ElementHoverHighlight } from "./ElementHoverHighlight";
import { ElementSelectedHighlight } from "./ElementSelectedHighlight";
import {
  IUseElementViewContextMenuArgs,
  useElementViewContextMenu,
} from "../../hooks/element/useElementViewContextMenu/useElementViewContextMenu";
import { websiteBuilderZLayers } from "../../../../websiteBuilderZLayers";
import { isWebsiteBuilderButton } from "../../../../nodes/element/websiteBuilderButton";
import { WebsiteBuilderButtonView } from "./button/WebsiteBuilderButtonView";
import { isWebsiteBuilderDivider } from "../../../../nodes/element/websiteBuilderDivider";
import { WebsiteBuilderDividerView } from "./divider/WebsiteBuilderDividerView";
import { isWebsiteBuilderHeading } from "../../../../nodes/element/websiteBuilderHeading";
import { WebsiteBuilderHeadingView } from "./heading/WebsiteBuilderHeadingView";
import { isWebsiteBuilderInput } from "../../../../nodes/element/websiteBuilderInput";
import { WebsiteBuilderInputView } from "./input/WebsiteBuilderInputView";
import { WebsiteBuilderParagraphView } from "./paragraph/WebsiteBuilderParagraphView";
import { isWebsiteBuilderSelect } from "../../../../nodes/element/websiteBuilderSelect";
import { WebsiteBuilderSelectView } from "./select/WebsiteBuilderSelectView";
import { isWebsiteBuilderParagraph } from "../../../../nodes/element/websiteBuilderParagraph";

interface IWebsiteBuilderElementViewProps {
  parentNode: TWebsiteBuilderElementParentNode;
  node: TWebsiteBuilderElement;
}

export interface IWebsiteBuilderElementViewBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ELEMENT_VIEW_MOTION_OPACITY = 0.7;

export function WebsiteBuilderElementView(props: IWebsiteBuilderElementViewProps) {
  const { parentNode, node } = props;
  const {
    type: elementType,
    position: elementPosition,
    dimensions: elementDimensions,
    zIndex: elementZIndex,
  } = node.element;

  const dispatch = useAppDispatch();

  const { treeRefMap, elementOperations, clipboard, designSpaceBoundaries } = useWebsiteBuilderContext();
  const treeRefMapRegistration = useTreeRefMapRegistration(node.id, treeRefMap);

  const elementStretch: IWebsiteBuilderElementStretch = isWebsiteBuilderStretchElementData(node.element)
    ? node.element.stretch
    : null;

  const selectedNode = useAppSelector(store => store.websiteBuilderStore.selectedNode);
  const elementSelected = selectedNode?.id === node.id;
  const editingMode = selectedNode?.editingMode;

  const elementMoving = elementSelected && editingMode === "element-move";
  const elementResizing = elementSelected && editingMode === "element-resize";
  const elementSettingsActive = elementSelected && editingMode === "element-settings";
  const elementEditingText = elementSettingsActive && isWebsiteBuilderTextElementType(elementType);
  const elementContextMenuActive = elementSelected && editingMode === "element-context-menu";

  const [elementHovered, setElementHovered] = useState<boolean>(false);
  const elementHoveredNotSelected = elementHovered && !elementSelected;

  const elementScreenPosition: IWebsiteBuilderNodePosition = useMemo(() => {
    const updatedElementScreenPosition = getElementScreenPosition(designSpaceBoundaries.leftX, elementPosition);

    if (elementStretch?.enabled) {
      updatedElementScreenPosition.x = elementStretch.margin;
    }

    return updatedElementScreenPosition;
  }, [designSpaceBoundaries.leftX, elementPosition, elementStretch]);

  const elementScreenDimensions: IWebsiteBuilderNodeDimensions = useMemo(() => {
    const updatedElementScreenDimensions = { ...elementDimensions };

    if (elementStretch?.enabled) {
      updatedElementScreenDimensions.width =
        designSpaceBoundaries.leftX + designSpaceBoundaries.rightX - elementStretch.margin * 2;
    }

    return updatedElementScreenDimensions;
  }, [elementDimensions, elementStretch, designSpaceBoundaries.leftX, designSpaceBoundaries.rightX]);

  const viewResizeArgs: IUseElementViewResizeArgs = useMemo(
    () => ({
      id: node.id,
      parentId: parentNode.id,
      type: elementType,
      position: elementPosition,
      screenPosition: elementScreenPosition,
      dimensions: elementDimensions,
      screenDimensions: elementScreenDimensions,
      selected: elementSelected,
      resizing: elementResizing,
      designSpaceBoundaries,
      stretch: elementStretch,
      treeRefMap,
    }),
    [
      node.id,
      parentNode.id,
      elementType,
      elementPosition,
      elementScreenPosition,
      elementDimensions,
      elementScreenDimensions,
      elementSelected,
      elementResizing,
      designSpaceBoundaries,
      elementStretch,
    ],
  );

  const { viewResizeScreenPosition, viewResizeScreenDimensions, viewResizeHandles, viewResizeIndicators } =
    useElementViewResize(viewResizeArgs);

  const viewMoveArgs: IUseElementViewMoveArgs = useMemo(
    () => ({
      id: node.id,
      parentId: parentNode.id,
      moving: elementMoving,
      position: elementPosition,
      screenPosition: elementScreenPosition,
      designSpaceBoundaries,
      stretch: elementStretch,
      treeRefMap,
    }),
    [
      node.id,
      parentNode.id,
      elementMoving,
      elementPosition,
      elementScreenPosition,
      designSpaceBoundaries,
      elementStretch,
    ],
  );

  const { viewMoveScreenPosition, viewMoveIndicator, handleViewMovePointerDown } = useElementViewMove(viewMoveArgs);

  const viewSettingsArgs: IUseElementViewSettingsArgs = useMemo(
    () => ({
      id: node.id,
      parentId: parentNode.id,
      selected: elementSelected,
      settingsActive: elementSettingsActive,
      node,
      treeRefMap,
    }),
    [node.id, parentNode.id, elementSelected, elementSettingsActive, node],
  );

  const { viewSettings, handleViewSettingsDoubleClick } = useElementViewSettings(viewSettingsArgs);

  const viewContextMenuArgs: IUseElementViewContextMenuArgs = useMemo(() => {
    return {
      node,
      parentNode,
      elementOperations,
      clipboard,
      selected: elementSelected,
      menuActive: elementContextMenuActive,
      treeRefMap,
    };
  }, [node, parentNode, elementOperations, clipboard, elementSelected, elementContextMenuActive]);

  const { viewContextMenu, handleViewRightClick } = useElementViewContextMenu(viewContextMenuArgs);

  const elementActivePosition: IWebsiteBuilderNodePosition = useMemo(() => {
    if (elementSelected && editingMode === "element-resize") {
      return viewResizeScreenPosition;
    } else if (elementSelected && editingMode === "element-move") {
      return viewMoveScreenPosition;
    } else {
      return elementScreenPosition;
    }
  }, [elementSelected, editingMode, viewResizeScreenPosition, viewMoveScreenPosition, elementScreenPosition]);

  const elementActiveDimensions: IWebsiteBuilderNodeDimensions = useMemo(() => {
    if (elementSelected && editingMode === "element-resize") {
      return viewResizeScreenDimensions;
    } else {
      return elementScreenDimensions;
    }
  }, [elementSelected, editingMode, viewResizeScreenDimensions, elementScreenDimensions]);

  const elementBoundingBox: IWebsiteBuilderElementViewBoundingBox = useMemo(() => {
    return {
      x: elementActivePosition.x,
      y: elementActivePosition.y,
      width: elementActiveDimensions.width,
      height: elementActiveDimensions.height,
    };
  }, [elementActivePosition, elementActiveDimensions]);

  function getElementViewPrecedence(): CSSProperties {
    let opacity: number = 1;
    let zIndex: number = elementZIndex;

    if (elementMoving || elementResizing) {
      opacity = ELEMENT_VIEW_MOTION_OPACITY;
    }

    if (elementMoving || elementResizing || elementSettingsActive || elementEditingText || elementContextMenuActive) {
      zIndex = websiteBuilderZLayers.designSpace.elementOverride;
    }

    return {
      opacity,
      zIndex,
    };
  }

  function renderChild(): JSX.Element {
    if (isWebsiteBuilderButton(node)) {
      return <WebsiteBuilderButtonView node={node} />;
    } else if (isWebsiteBuilderDivider(node)) {
      return <WebsiteBuilderDividerView node={node} />;
    } else if (isWebsiteBuilderHeading(node)) {
      return <WebsiteBuilderHeadingView node={node} editingText={elementEditingText} />;
    } else if (isWebsiteBuilderInput(node)) {
      return <WebsiteBuilderInputView node={node} />;
    } else if (isWebsiteBuilderParagraph(node)) {
      return <WebsiteBuilderParagraphView node={node} editingText={elementEditingText} />;
    } else if (isWebsiteBuilderSelect(node)) {
      return <WebsiteBuilderSelectView node={node} />;
    } else {
      return null;
    }
  }

  return (
    <>
      <div
        ref={treeRefMapRegistration}
        className={classNames(WEBSITE_BUILDER_ELEMENT_CLASS_NAME, "editor-element", {
          "element-hovered": elementHovered,
          "element-selected": elementSelected,
        })}
        style={{
          left: elementBoundingBox.x,
          top: elementBoundingBox.y,
          width: elementBoundingBox.width,
          height: elementBoundingBox.height,
          ...getElementViewPrecedence(),
        }}
        onContextMenu={e => {
          e.preventDefault();

          handleViewRightClick({ x: e.clientX, y: e.clientY });
        }}
        onMouseOver={() => setElementHovered(true)}
        onMouseOut={() => setElementHovered(false)}
        onDoubleClick={e => {
          e.stopPropagation();

          if (!elementEditingText) {
            handleViewSettingsDoubleClick();
          }
        }}
        onPointerDown={e => {
          e.stopPropagation();

          if (!elementSelected) {
            dispatch(
              websiteBuilderSelectNode({
                selectedNode: {
                  parentId: parentNode.id,
                  id: node.id,
                  editingMode: "none",
                },
              }),
            );
          }

          if (e.button === 0) {
            if (elementContextMenuActive) {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    editingMode: "none",
                  },
                }),
              );
            }
            if (!elementEditingText) {
              handleViewMovePointerDown(e);
            }
          }
        }}
      >
        {renderChild()}
        {viewMoveIndicator}
        {viewResizeIndicators}
        {viewSettings}
        {viewContextMenu}
      </div>

      <ElementHoverTitle
        hoveredNotSelected={elementHoveredNotSelected}
        boundingBox={elementBoundingBox}
        type={elementType}
      />
      <ElementHoverHighlight hoveredNotSelected={elementHoveredNotSelected} boundingBox={elementBoundingBox} />
      <ElementSelectedHighlight
        selected={elementSelected}
        boundingBox={elementBoundingBox}
        viewResizeHandles={viewResizeHandles}
      />
    </>
  );
}
